import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Minus, X, Check, ShoppingBag, Store, ReceiptText, Loader2, ChevronRight,
} from "lucide-react";
import { http, rupiah, guestToken, apiError } from "@/lib/api";
import ThemeScope from "@/components/ThemeScope";
import { PRESETS } from "@/lib/themes";


function SocialLinks({ links }) {
  const items = [
    [
      "facebook",
      "Facebook",
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
        <path d="M13.5 22v-8h2.7l.4-3h-3.1V9.1c0-.9.2-1.5 1.5-1.5h1.7V4.9c-.3 0-1.3-.1-2.5-.1-2.5 0-4.2 1.5-4.2 4.3V11H7.2v3H10v8h3.5Z" />
      </svg>,
    ],
    [
      "instagram",
      "Instagram",
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
      </svg>,
    ],
    [
      "whatsapp",
      "WhatsApp",
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
        <path d="M12 2a9.7 9.7 0 0 0-8.4 14.6L2 22l5.6-1.5A9.8 9.8 0 1 0 12 2Zm0 17.6c-1.5 0-3-.4-4.2-1.2l-.3-.2-3.3.9.9-3.2-.2-.3A7.7 7.7 0 1 1 12 19.6Zm4.2-5.8c-.2-.1-1.4-.7-1.6-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1-1.5-.7-2.5-1.3-3.5-3-.3-.5.3-.5.8-1.5.1-.2 0-.4 0-.5l-.7-1.7c-.2-.5-.4-.4-.6-.4H8c-.2 0-.5.1-.7.3-.8.8-1.2 1.9-1.2 3 0 1.8 1.3 3.6 1.5 3.8.2.2 2.6 4 6.4 5.5.9.4 1.6.6 2.1.7.9.3 1.7.2 2.4.1.7-.1 2.3-.9 2.6-1.8.3-.9.3-1.6.2-1.8-.1-.1-.3-.2-.5-.3Z" />
      </svg>,
    ],
    [
      "google_maps",
      "Google Maps",
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
        <path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5Z" />
      </svg>,
    ],
  ];

  const visible = items.filter(([key]) => {
    const value = links?.[key];
    return typeof value === "string" && value.trim();
  });

  if (!visible.length) return null;

  return (
    <div className="mt-3 flex items-center justify-center gap-2.5">
      {visible.map(([key, label, icon]) => (
        <a
          key={key}
          href={links[key].trim()}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          title={label}
          className="grid h-9 w-9 place-items-center rounded-full border border-border bg-card text-muted-foreground transition hover:border-primary hover:text-primary active:scale-95"
        >
          {icon}
        </a>
      ))}
    </div>
  );
}

export default function CustomerMenu() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [cart, setCart] = useState([]);
  const [activeCat, setActiveCat] = useState("");
  const [config, setConfig] = useState(null); // product being configured
  const [detailProduct, setDetailProduct] = useState(null);
  const [editingCartKey, setEditingCartKey] = useState(null);
  const [modQty, setModQty] = useState({});
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [checkout, setCheckout] = useState(false);
  const [showEditOrderConfirm, setShowEditOrderConfirm] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [method, setMethod] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyItems, setHistoryItems] = useState(null);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [waModal, setWaModal] = useState(null);
  const [sendingWa, setSendingWa] = useState(false);
  const [voucherCode, setVoucherCode] = useState("");
  const [appliedVoucher, setAppliedVoucher] = useState(null);
  const [voucherLoading, setVoucherLoading] = useState(false);
  const navigate = useNavigate();
  const catRefs = useRef({});
  const categoryNavRef = useRef(null);
  const categoryPillRefs = useRef({});

  useEffect(() => {
    http.get(`/store/${token}`).then(({ data }) => {
      setData(data);
      if (data.categories[0]) setActiveCat(data.categories[0].id);
      try {
        const saved = JSON.parse(localStorage.getItem(`wm_cart_${token}`) || "[]");
        if (Array.isArray(saved)) setCart(saved);
      } catch {}
    }).catch((e) => setErr(apiError(e)));
  }, [token]);

  useEffect(() => {
    localStorage.setItem(`wm_cart_${token}`, JSON.stringify(cart));
  }, [cart, token]);

  // =========================================================
  // SYNC ACTIVE CATEGORY WITH MANUAL SCROLL
  // =========================================================
  // Saat user scroll manual, kategori aktif di sticky navigation
  // otomatis mengikuti section yang sedang terlihat.
  useEffect(() => {
    if (!data?.categories?.length) return;

    let ticking = false;

    const updateActiveCategory = () => {
      ticking = false;

      const navHeight =
        categoryNavRef.current?.getBoundingClientRect().height || 64;

      // Garis virtual sedikit di bawah sticky category bar.
      const activationLine = navHeight + 28;

      let currentId = data.categories[0]?.id || "";

      for (const category of data.categories) {
        const el = catRefs.current[category.id];
        if (!el) continue;

        const rect = el.getBoundingClientRect();

        if (rect.top <= activationLine) {
          currentId = category.id;
        } else {
          break;
        }
      }

      setActiveCat((prev) =>
        prev === currentId ? prev : currentId
      );
    };

    const handleScroll = () => {
      if (ticking) return;

      ticking = true;
      window.requestAnimationFrame(updateActiveCategory);
    };

    updateActiveCategory();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [data]);

  // Kalau kategori aktif berubah karena scroll manual,
  // pill aktif ikut digeser ke tengah secara horizontal.
  useEffect(() => {
    if (!activeCat) return;

    const pill = categoryPillRefs.current[activeCat];

    if (!pill) return;

    pill.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [activeCat]);

  const tokens = data?.store?.theme?.tokens || PRESETS.midnight_noodle.tokens;
  const cashEnabled = data?.store?.payment_methods?.cash_enabled !== false;
  const isGrid = data?.store?.menu_layout === "grid";
  const logoRound = data?.store?.logo_shape === "circle" ? "rounded-full" : "rounded-2xl";
  const total = useMemo(() => cart.reduce((s, i) => s + i.unit_price * i.qty, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);
  const voucherDiscount = Math.max(0, Math.min(total, Number(appliedVoucher?.discount_amount || 0)));
  const finalTotal = Math.max(0, total - voucherDiscount);

  function productImageStyle(product) {
    const x = Math.max(
      0,
      Math.min(100, Number(product?.image_position_x ?? 50)),
    );
    const y = Math.max(
      0,
      Math.min(100, Number(product?.image_position_y ?? 50)),
    );
    const zoom = Math.max(
      1,
      Math.min(2.5, Number(product?.image_zoom ?? 1)),
    );

    return {
      objectPosition: `${x}% ${y}%`,
      transform: `scale(${zoom})`,
      transformOrigin: `${x}% ${y}%`,
    };
  }

  function findProduct(productId) {
    for (const category of data?.categories || []) {
      const product = (category.products || []).find(
        (item) => item.id === productId,
      );

      if (product) return product;
    }

    return null;
  }

  function openProduct(p) {
    if (!p.available) return;

    if (!p.modifier_groups?.length) {
      addToCart(p, [], 1, "");
      toast.success(`${p.name} ditambahkan`);
      return;
    }

    const defaults = {};

    p.modifier_groups.forEach((g) => {
      defaults[g.id] = {};
      g.options.forEach((o) => {
        defaults[g.id][o.id] = 0;
      });
    });

    setEditingCartKey(null);
    setModQty(defaults);
    setQty(1);
    setNote("");
    setConfig(p);
  }

  function editCartItem(item) {
    const product = findProduct(item.product_id);

    if (!product) {
      toast.error("Produk tidak ditemukan.");
      return;
    }

    if (!product.modifier_groups?.length) {
      toast.error("Produk ini tidak memiliki varian / topping.");
      return;
    }

    const defaults = {};

    product.modifier_groups.forEach((g) => {
      defaults[g.id] = {};

      g.options.forEach((o) => {
        defaults[g.id][o.id] = 0;
      });
    });

    (item.modifiers || []).forEach((modifier) => {
      for (const group of product.modifier_groups) {
        if (
          Object.prototype.hasOwnProperty.call(
            defaults[group.id] || {},
            modifier.option_id,
          )
        ) {
          defaults[group.id][modifier.option_id] =
            Number(modifier.qty || 0);
          break;
        }
      }
    });

    setEditingCartKey(item.key);
    setModQty(defaults);
    setQty(item.qty || 1);
    setNote(item.note || "");
    setConfig(product);
    setCheckout(false);
  }

  function optQty(gid, oid) {
    return modQty?.[gid]?.[oid] || 0;
  }

  function setOpt(gid, oid, v) {
    setModQty((c) => ({
      ...c,
      [gid]: {
        ...(c[gid] || {}),
        [oid]: Math.max(0, Math.min(20, v)),
      },
    }));
  }

  function toggleModifierOption(group, option) {
    const currentQty = optQty(group.id, option.id);
    const selected = currentQty > 0;
    const minSelect = Math.max(0, Number(group.min_select || 0));
    const maxSelect = Math.max(1, Number(group.max_select || 1));

    setModQty((current) => {
      const groupState = {
        ...(current[group.id] || {}),
      };

      // SINGLE CHOICE
      // Max = 1 berarti hanya satu opsi boleh aktif.
      if (maxSelect === 1) {
        if (selected) {
          // Kalau wajib pilih satu, pilihan aktif tidak boleh dikosongkan.
          if (minSelect >= 1) {
            return current;
          }

          groupState[option.id] = 0;
        } else {
          // Matikan semua pilihan lain, lalu aktifkan pilihan baru.
          Object.keys(groupState).forEach((id) => {
            groupState[id] = 0;
          });

          groupState[option.id] = 1;
        }

        return {
          ...current,
          [group.id]: groupState,
        };
      }

      // MULTI CHOICE
      // Max > 1 membatasi jumlah jenis opsi yang dipilih.
      if (selected) {
        groupState[option.id] = 0;

        return {
          ...current,
          [group.id]: groupState,
        };
      }

      const selectedCount = Object.values(groupState).filter(
        (qtyValue) => Number(qtyValue) > 0,
      ).length;

      if (selectedCount >= maxSelect) {
        toast.error(`${group.name}: maksimal pilih ${maxSelect} opsi`);
        return current;
      }

      groupState[option.id] = 1;

      return {
        ...current,
        [group.id]: groupState,
      };
    });
  }

  const configPrice = useMemo(() => {
    if (!config) return 0;
    let extra = 0;
    config.modifier_groups.forEach((g) => g.options.forEach((o) => extra += o.price_delta * optQty(g.id, o.id)));
    return config.price + extra;
  }, [config, modQty]);

  function confirmProduct() {
    for (const g of config.modifier_groups) {
      const chosen = g.options.filter(
        (o) => optQty(g.id, o.id) > 0,
      ).length;

      const minSelect = Math.max(0, Number(g.min_select || 0));
      const maxSelect = Math.max(1, Number(g.max_select || 1));

      if (chosen < minSelect) {
        toast.error(`${g.name}: pilih minimal ${minSelect}`);
        return;
      }

      if (chosen > maxSelect) {
        toast.error(`${g.name}: maksimal pilih ${maxSelect}`);
        return;
      }
    }
    const mods = [];

    config.modifier_groups.forEach((g) =>
      g.options.forEach((o) => {
        const q = optQty(g.id, o.id);

        if (q > 0) {
          mods.push({
            option_id: o.id,
            name: o.name,
            price_delta: o.price_delta,
            qty: q,
          });
        }
      }),
    );

    if (editingCartKey) {
      const cleanNote = note.trim();
      const key = `${config.id}:${mods
        .map((m) => m.option_id + "x" + m.qty)
        .sort()
        .join(",")}:${cleanNote}`;

      const extra = mods.reduce(
        (sum, modifier) =>
          sum + modifier.price_delta * modifier.qty,
        0,
      );

      const unit = config.price + extra;

      setCart((current) =>
        current.map((item) =>
          item.key === editingCartKey
            ? {
                ...item,
                key,
                name: config.name,
                image_url: config.image_url || "",
                image_position_x: Number(config.image_position_x ?? 50),
                image_position_y: Number(config.image_position_y ?? 50),
                image_zoom: Number(config.image_zoom ?? 1),
                unit_price: unit,
                qty,
                note: cleanNote,
                modifiers: mods,
              }
            : item,
        ),
      );

      toast.success(`${config.name} diperbarui`);
      setEditingCartKey(null);
      setConfig(null);
      setCheckout(true);
      return;
    }

    addToCart(config, mods, qty, note.trim());
    toast.success(`${config.name} ditambahkan`);
    setConfig(null);
  }

  function addToCart(p, mods, q, n) {
    const key = `${p.id}:${mods.map((m) => m.option_id + "x" + m.qty).sort().join(",")}:${n}`;
    const extra = mods.reduce((s, m) => s + m.price_delta * m.qty, 0);
    const unit = p.price + extra;
    setCart((c) => {
      const idx = c.findIndex((i) => i.key === key);
      if (idx >= 0) return c.map((i, x) => x === idx ? { ...i, qty: Math.min(50, i.qty + q) } : i);
      return [
        ...c,
        {
          key,
          product_id: p.id,
          name: p.name,
          image_url: p.image_url || "",
          image_position_x: Number(p.image_position_x ?? 50),
          image_position_y: Number(p.image_position_y ?? 50),
          image_zoom: Number(p.image_zoom ?? 1),
          unit_price: unit,
          qty: q,
          note: n,
          modifiers: mods,
        },
      ];
    });
  }

  function changeQty(key, d) {
    setCart((c) => c.map((i) => i.key === key ? { ...i, qty: i.qty + d } : i).filter((i) => i.qty > 0));
  }

  async function validateVoucher({ silent = false } = {}) {
    const code = String(voucherCode || appliedVoucher?.code || "").trim().toUpperCase();

    if (!code) {
      if (!silent) toast.error("Masukkan kode voucher");
      return false;
    }

    setVoucherLoading(true);
    try {
      const { data: result } = await http.post("/vouchers/validate", {
        code,
        subtotal: total,
        payment_method: method || "",
      });

      setVoucherCode(result.code || code);
      setAppliedVoucher(result);

      if (!silent) {
        toast.success(`Voucher ${result.code} berhasil dipakai`);
      }

      return true;
    } catch (e) {
      setAppliedVoucher(null);
      if (!silent) {
        toast.error(apiError(e));
      }
      return false;
    } finally {
      setVoucherLoading(false);
    }
  }

  function removeVoucher() {
    setAppliedVoucher(null);
    setVoucherCode("");
    toast.success("Voucher dilepas");
  }

  useEffect(() => {
    if (!appliedVoucher?.code || total <= 0) return;

    const timer = window.setTimeout(() => {
      validateVoucher({ silent: true });
    }, 250);

    return () => window.clearTimeout(timer);
    // appliedVoucher.code sengaja tidak dimasukkan agar tidak loop setelah hasil validasi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, method]);

  async function submitOrder() {
    if (name.trim().length < 2) return toast.error("Nama minimal 2 karakter");
    if (phone.replace(/\D/g, "").length < 8) return toast.error("Nomor WhatsApp belum valid");
    if (!method) return toast.error("Pilih metode pembayaran");
    setSubmitting(true);
    try {
      const { data: res } = await http.post("/checkout", {
        table_token: token, guest_token: guestToken(), customer_name: name,
        customer_phone: phone, payment_method: method, return_origin: window.location.origin,
        voucher_code: appliedVoucher?.code || "",
        items: cart.map((i) => ({ product_id: i.product_id, qty: i.qty, note: i.note,
          modifiers: i.modifiers.map((m) => ({ option_id: m.option_id, qty: m.qty })) })),
      });
      const summary = { id: res.order_id, total: res.total, subtotal: res.subtotal, discount_amount: res.discount_amount, voucher_code: res.voucher_code, payment_method: res.payment_method, payment_status: res.payment_status, pay_url: res.redirect_url, midtrans: res.midtrans, table_name: data.table.name, created_at: new Date().toISOString() };
      const saved = JSON.parse(localStorage.getItem("wm_orders") || "[]");
      localStorage.setItem("wm_orders", JSON.stringify([summary, ...saved].slice(0, 50)));
      setCart([]); setCheckout(false); setMethod(""); setAppliedVoucher(null); setVoucherCode("");
      if (res.payment_method === "qris") {
        if (res.redirect_url?.startsWith("http")) {
          window.location.assign(res.redirect_url);
        } else {
          navigate(`/pay/${res.order_id}`);
        }
        return;
      }

      if (res.payment_method === "cash") {
        toast.success(`Pesanan ${res.order_id} diterima!`);

        setTimeout(() => {
          navigate("/history");
        }, 500);

        return;
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  }

  async function openHistory() {
    setShowHistory(true);

    try {
      const { data } = await http.get(
        `/orders/history/${guestToken()}`
      );

      const latest = [...(data.items || [])].sort(
        (a, b) =>
          new Date(b.created_at).getTime() -
          new Date(a.created_at).getTime()
      );

      setHistoryItems(latest);
    } catch {
      setHistoryItems([]);
    }
  }
  function resumePay(o) {
    if (o.pay_url && o.pay_url.startsWith("http")) window.location.assign(o.pay_url);
    else navigate(`/pay/${o.id}`);
  }
  function sendInvoiceWa(o) { setWaModal({ order: o, phone: o.customer_phone || "" }); }
  async function submitWa() {
    if (!waModal || !waModal.phone.trim()) return toast.error("Masukkan nomor WhatsApp");
    setSendingWa(true);
    try { await http.post(`/orders/${waModal.order.id}/send-invoice`, { phone: waModal.phone }); toast.success("Invoice terkirim ke WhatsApp ✅"); setWaModal(null); openHistory(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSendingWa(false); }
  }

  function scrollToCat(id) {
    const el = catRefs.current[id];

    if (!el) return;

    setActiveCat(id);

    const navHeight =
      categoryNavRef.current?.getBoundingClientRect().height || 64;

    const y =
      el.getBoundingClientRect().top +
      window.scrollY -
      navHeight -
      16;

    window.scrollTo({
      top: Math.max(0, y),
      behavior: "smooth",
    });
  }

  if (err) return (
    <div className="flex min-h-dvh items-center justify-center bg-neutral-950 p-8 text-center text-neutral-300">
      <div><Store className="mx-auto mb-4 text-neutral-600" size={40} /><p className="font-heading text-xl">{err}</p></div>
    </div>
  );
  if (!data) return (
    <div className="flex min-h-dvh items-center justify-center bg-neutral-950"><Loader2 className="wm-spin text-neutral-500" size={32} /></div>
  );

  return (
    <ThemeScope tokens={tokens} className="min-h-dvh">
      <div className="phone-frame relative pb-32">
        {/* Hero */}
        <div className="relative h-64 overflow-hidden">
          <img src={data.store.hero_image || "https://images.unsplash.com/photo-1526318896980-cf78c088247c?q=85&w=1200"}
            alt="hero" className="h-full w-full object-cover" style={{ objectPosition: data.store.hero_position || "50% 50%" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-black/20" />
          <button data-testid="history-btn" onClick={openHistory} className="absolute right-4 top-4 z-10 flex items-center gap-1.5 rounded-full bg-black/45 px-3 py-2 text-sm font-bold text-white backdrop-blur-md transition-transform active:scale-90">
            <span className="text-base leading-none">🧾</span> Riwayat
          </button>
          <div className="absolute inset-x-0 bottom-0 p-6">
            <div className="mb-3 flex items-center gap-3">
              <div className={`grid h-12 w-12 place-items-center overflow-hidden bg-card ${logoRound}`}>
                {data.store.logo_url ? <img src={data.store.logo_url} alt="logo" className="h-full w-full object-cover" /> : <Store size={22} className="text-primary" />}
              </div>
              <div>
                <div className="font-heading text-lg font-extrabold leading-tight text-white">{data.store.name}</div>
                <span className="inline-flex items-center rounded-full bg-primary px-2.5 py-0.5 text-[11px] font-bold text-primary-foreground">{data.table.name}</span>
              </div>
            </div>
            <h1 className="font-heading text-3xl font-extrabold leading-none tracking-tight text-white">
              {data.store.tagline}
            </h1>
          </div>
        </div>

        {/* Category pills */}
        <div
          ref={categoryNavRef}
          className="sticky top-0 z-20 border-b border-border bg-background/85 backdrop-blur-xl"
        >
          <div className="no-scrollbar flex gap-2 overflow-x-auto px-4 py-3">
            {data.categories.map((c) => (
              <button
                key={c.id}
                ref={(el) => (categoryPillRefs.current[c.id] = el)}
                data-testid={`cat-pill-${c.id}`}
                onClick={() => scrollToCat(c.id)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold transition-transform active:scale-95 ${activeCat === c.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        {/* Products */}
        <div className="space-y-8 px-4 pt-5">
          {data.categories.map((c) => (
            <section key={c.id} ref={(el) => (catRefs.current[c.id] = el)}>
              <h2 className="mb-3 font-heading text-2xl font-extrabold tracking-tight">{c.name}</h2>
              <div className={isGrid ? "grid grid-cols-2 gap-3" : "space-y-3"}>
                {c.products.map((p, idx) => isGrid ? (
                  <article key={p.id} data-testid={`product-${p.id}`} className="wm-rise flex flex-col overflow-hidden rounded-3xl border border-border bg-card" style={{ animationDelay: `${idx * 40}ms` }}>
                    <button
                      type="button"
                      onClick={() => setDetailProduct(p)}
                      className="relative block h-28 w-full overflow-hidden bg-muted text-left"
                      aria-label={`Lihat detail ${p.name}`}
                    >
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt={p.name}
                          className="h-full w-full object-cover transition-transform duration-200"
                          style={productImageStyle(p)}
                        />
                      ) : (
                        <div className="grid h-full place-items-center text-muted-foreground transition-transform duration-200 active:scale-[0.98]">
                          <Store size={20} />
                        </div>
                      )}
                      {p.featured && <span className="absolute left-1.5 top-1.5 rounded-full bg-primary px-1.5 py-0.5 text-[8px] font-black text-primary-foreground">★ FAV</span>}
                    </button>
                    <div className="flex flex-1 flex-col p-3">
                      <h3 className="line-clamp-1 font-heading text-sm font-bold leading-tight text-card-foreground">{p.name}</h3>
                      <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-muted-foreground">{p.description}</p>
                      <div className="mt-auto pt-2">
                        <div className="font-heading text-base font-extrabold text-primary">{rupiah(p.price)}</div>
                        <button data-testid={`add-${p.id}`} disabled={!p.available} onClick={() => openProduct(p)} className="mt-1.5 flex w-full items-center justify-center gap-1 rounded-full bg-primary py-2 text-xs font-bold text-primary-foreground transition-transform active:scale-90 disabled:opacity-40">{p.available ? <><Plus size={14} /> Tambah</> : "Habis"}</button>
                      </div>
                    </div>
                  </article>
                ) : (
                  <article key={p.id} data-testid={`product-${p.id}`}
                    className="wm-rise flex gap-3 rounded-3xl border border-border bg-card p-3" style={{ animationDelay: `${idx * 40}ms` }}>
                    <button
                      type="button"
                      onClick={() => setDetailProduct(p)}
                      className="relative h-24 w-24 flex-none overflow-hidden rounded-2xl bg-muted text-left"
                      aria-label={`Lihat detail ${p.name}`}
                    >
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt={p.name}
                          className="h-full w-full object-cover transition-transform duration-200"
                          style={productImageStyle(p)}
                        />
                      ) : (
                        <div className="grid h-full place-items-center text-muted-foreground transition-transform duration-200 active:scale-[0.98]">
                          <Store size={20} />
                        </div>
                      )}
                      {p.featured && <span className="absolute left-1 top-1 rounded-full bg-primary px-2 py-0.5 text-[9px] font-black text-primary-foreground">★ FAV</span>}
                    </button>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <h3 className="font-heading text-base font-bold leading-tight text-card-foreground">{p.name}</h3>
                      <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">{p.description}</p>
                      <div className="mt-auto flex items-center justify-between pt-2">
                        <span className="font-heading text-lg font-extrabold text-primary">{rupiah(p.price)}</span>
                        <button data-testid={`add-${p.id}`} disabled={!p.available} onClick={() => openProduct(p)}
                          className="flex items-center gap-1 rounded-full bg-primary px-3.5 py-2 text-xs font-bold text-primary-foreground transition-transform active:scale-90 disabled:opacity-40">
                          {p.available ? <><Plus size={15} /> Tambah</> : "Habis"}
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-8 pb-6 text-center text-[11px] text-muted-foreground">
          <div>
            © {new Date().getFullYear()} {data.store.name} — {data.store.footer_text || "QR Table Ordering"}
          </div>
          <SocialLinks links={data.store.social_links} />
        </div>

        {/* Cart FAB */}
        {cartCount > 0 && !config && !checkout && (
          <div className="fixed inset-x-0 bottom-0 z-30 mx-auto max-w-[480px] p-4">
            <button data-testid="cart-fab" onClick={() => setCheckout(true)}
              className="flex w-full items-center justify-between rounded-2xl bg-primary px-5 py-4 text-primary-foreground shadow-[0_10px_40px_rgba(0,0,0,0.4)] transition-transform active:scale-95">
              <span className="flex items-center gap-2 font-bold"><ShoppingBag size={20} /> {cartCount} item</span>
              <span className="flex items-center gap-2 font-heading text-lg font-extrabold">{rupiah(total)} <ChevronRight size={20} /></span>
            </button>
          </div>
        )}

        {/* Product detail sheet */}
        {detailProduct && (
          <Sheet onClose={() => setDetailProduct(null)}>
            <div className="overflow-hidden rounded-3xl border border-border bg-muted">
              <div className="aspect-[16/10] w-full bg-muted">
                {detailProduct.image_url ? (
                  <img
                    src={detailProduct.image_url}
                    alt={detailProduct.name}
                    className="h-full w-full object-cover"
                    style={productImageStyle(detailProduct)}
                  />
                ) : (
                  <div className="grid h-full place-items-center text-muted-foreground">
                    <Store size={42} />
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="font-heading text-2xl font-extrabold tracking-tight text-card-foreground">
                    {detailProduct.name}
                  </h2>

                  {detailProduct.featured && (
                    <span className="mt-2 inline-flex rounded-full bg-primary/15 px-2.5 py-1 text-[10px] font-black text-primary">
                      ★ FAVORIT
                    </span>
                  )}
                </div>

                <div className="flex-none font-heading text-xl font-extrabold text-primary">
                  {rupiah(detailProduct.price)}
                </div>
              </div>

              <div className="mt-4 rounded-2xl border border-border bg-muted/50 p-4">
                <div className="mb-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  Deskripsi
                </div>
                <p className="whitespace-pre-wrap break-words text-sm leading-6 text-card-foreground">
                  {detailProduct.description?.trim() || "Belum ada deskripsi untuk produk ini."}
                </p>
              </div>

              <button
                type="button"
                disabled={!detailProduct.available}
                onClick={() => {
                  const product = detailProduct;
                  setDetailProduct(null);
                  openProduct(product);
                }}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 font-bold text-primary-foreground transition-transform active:scale-95 disabled:opacity-40"
              >
                {detailProduct.available ? (
                  <>
                    <Plus size={16} />
                    Tambah · {rupiah(detailProduct.price)}
                  </>
                ) : (
                  "Habis"
                )}
              </button>
            </div>
          </Sheet>
        )}

        {/* Modifier sheet */}
        {config && (
          <Sheet
            onClose={() => {
              const wasEditing = Boolean(editingCartKey);
              setConfig(null);
              setEditingCartKey(null);

              if (wasEditing) {
                setCheckout(true);
              }
            }}
          >
            <h2 className="font-heading text-2xl font-extrabold tracking-tight text-card-foreground">{config.name}</h2>
            <p className="mt-1 text-sm text-muted-foreground">Mulai {rupiah(config.price)}</p>
            <div className="mt-5 space-y-5">
              {config.modifier_groups.map((g) => (
                <div key={g.id}>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <div>
                      <b className="font-heading text-base">{g.name}</b>
                      <div className="mt-0.5 text-[10px] text-muted-foreground">
                        {Number(g.max_select || 1) === 1
                          ? Number(g.min_select || 0) > 0
                            ? "Pilih tepat 1"
                            : "Pilih maksimal 1"
                          : `Maksimal ${g.max_select} pilihan`}
                      </div>
                    </div>

                    {g.min_select > 0 && (
                      <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold text-primary">
                        WAJIB
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    {g.options.map((o) => {
                      const q = optQty(g.id, o.id); const sel = q > 0;
                      return (
                        <div key={o.id} className={`flex items-center gap-3 rounded-2xl border p-3 transition-colors ${sel ? "border-primary bg-primary/5" : "border-border bg-muted"}`}>
                          <button
                            data-testid={`opt-${o.id}`}
                            onClick={() => toggleModifierOption(g, o)}
                            className={`grid h-6 w-6 flex-none place-items-center border-2 ${
                              Number(g.max_select || 1) === 1
                                ? "rounded-full"
                                : "rounded-lg"
                            } ${
                              sel
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-muted-foreground/40"
                            }`}
                          >
                            {sel && <Check size={14} />}
                          </button>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-card-foreground">{o.name}</div>
                            <div className="text-xs text-muted-foreground">{o.price_delta > 0 ? `+${rupiah(o.price_delta)}` : "Gratis"}</div>
                          </div>
                          {sel && Number(g.max_select || 1) > 1 && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setOpt(g.id, o.id, q - 1)}
                                className="grid h-8 w-8 place-items-center rounded-lg bg-background"
                              >
                                <Minus size={14} />
                              </button>
                              <b className="w-4 text-center text-sm">{q}</b>
                              <button
                                onClick={() => setOpt(g.id, o.id, q + 1)}
                                className="grid h-8 w-8 place-items-center rounded-lg bg-background"
                              >
                                <Plus size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div>
                <b className="font-heading text-base">Catatan</b>
                <textarea data-testid="product-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={200}
                  placeholder="Cth: jangan pakai bawang" className="mt-2 w-full resize-none rounded-2xl border border-border bg-muted p-3 text-sm outline-none" />
              </div>
            </div>
            <div className="sticky bottom-0 mt-5 bg-card pt-3">
              <button data-testid="confirm-add-btn" onClick={confirmProduct}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 font-bold text-primary-foreground transition-transform active:scale-95">
                {editingCartKey ? "Update Pesanan" : "Tambah"} · {rupiah(configPrice * qty)}
              </button>
            </div>
          </Sheet>
        )}

        {/* Checkout sheet */}
        {checkout && (
          <Sheet onClose={() => setCheckout(false)}>
            <h2 className="font-heading text-2xl font-extrabold tracking-tight text-card-foreground">Checkout</h2>
            <p className="mt-1 text-sm text-muted-foreground">{data.table.name} · {cartCount} item</p>
            <div className="mt-4 space-y-2">
              {cart.map((i) => {
                const liveProduct = findProduct(i.product_id);
                const imageUrl =
                  i.image_url ||
                  liveProduct?.image_url ||
                  "";
                const hasModifiers =
                  Boolean(
                    liveProduct?.modifier_groups?.length,
                  );

                return (
                  <div
                    key={i.key}
                    className="rounded-2xl border border-border bg-muted p-3"
                  >
                    <div className="flex gap-3">
                      <div className="h-20 w-20 flex-none overflow-hidden rounded-2xl bg-background">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={i.name}
                            className="h-full w-full object-cover"
                            style={productImageStyle(liveProduct || i)}
                          />
                        ) : (
                          <div className="grid h-full place-items-center text-muted-foreground">
                            <Store size={18} />
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <b className="font-heading text-sm leading-tight text-card-foreground">
                            {i.name}
                          </b>

                          <b className="flex-none text-sm text-card-foreground">
                            {rupiah(i.unit_price * i.qty)}
                          </b>
                        </div>

                        {i.modifiers.length > 0 && (
                          <div className="mt-1 text-xs leading-relaxed text-muted-foreground">
                            Tambahan Menu:{" "}
                            {i.modifiers
                              .map((m) =>
                                m.qty > 1
                                  ? `${m.name} ×${m.qty}`
                                  : m.name,
                              )
                              .join(", ")}
                          </div>
                        )}

                        {i.note && (
                          <div className="mt-0.5 text-xs italic text-muted-foreground">
                            "{i.note}"
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-3 flex items-center justify-between gap-3">
                      {hasModifiers ? (
                        <button
                          type="button"
                          data-testid={`edit-cart-${i.key}`}
                          onClick={() => editCartItem(i)}
                          className="flex items-center gap-1.5 rounded-xl border border-border bg-background px-3 py-2 text-xs font-bold text-card-foreground transition active:scale-95"
                        >
                          <ReceiptText size={14} />
                          Edit
                        </button>
                      ) : (
                        <div />
                      )}

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() =>
                            changeQty(i.key, -1)
                          }
                          className="grid h-8 w-8 place-items-center rounded-full bg-background"
                        >
                          <Minus size={13} />
                        </button>

                        <b className="w-5 text-center text-sm">
                          {i.qty}
                        </b>

                        <button
                          onClick={() =>
                            changeQty(i.key, 1)
                          }
                          className="grid h-8 w-8 place-items-center rounded-full bg-background"
                        >
                          <Plus size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              data-testid="edit-order-btn"
              onClick={() => setShowEditOrderConfirm(true)}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border border-border bg-muted py-3 text-sm font-bold text-card-foreground transition-transform active:scale-95"
            >
              <ReceiptText size={16} />
              Ubah Pesanan
            </button>

            <div className="mt-5 space-y-3">
              <input data-testid="checkout-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nama kamu"
                className="w-full rounded-2xl border border-border bg-muted p-3.5 text-sm outline-none" />
              <input data-testid="checkout-phone" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="Nomor WhatsApp (cth 0812...)"
                className="w-full rounded-2xl border border-border bg-muted p-3.5 text-sm outline-none" />

              <div className="rounded-2xl border border-border bg-muted p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-bold text-card-foreground">Punya kode voucher?</div>
                    <div className="text-[11px] text-muted-foreground">Masukkan kode promo sebelum membuat pesanan.</div>
                  </div>
                  <div className="text-xl">🏷️</div>
                </div>

                {!appliedVoucher ? (
                  <div className="flex gap-2">
                    <input
                      data-testid="voucher-code"
                      value={voucherCode}
                      onChange={(e) => setVoucherCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ""))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          validateVoucher();
                        }
                      }}
                      placeholder="Contoh: NGEBLAR10"
                      maxLength={30}
                      className="min-w-0 flex-1 rounded-xl border border-border bg-background px-3 py-2.5 text-sm font-bold uppercase outline-none"
                    />
                    <button
                      type="button"
                      data-testid="apply-voucher"
                      onClick={() => validateVoucher()}
                      disabled={voucherLoading || !voucherCode.trim()}
                      className="flex min-w-[78px] items-center justify-center rounded-xl bg-primary px-3 py-2.5 text-xs font-bold text-primary-foreground disabled:opacity-50"
                    >
                      {voucherLoading ? <Loader2 className="wm-spin" size={15} /> : "Pakai"}
                    </button>
                  </div>
                ) : (
                  <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-xs font-extrabold text-emerald-500">✓ {appliedVoucher.code} dipakai</div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">{appliedVoucher.name}</div>
                      </div>
                      <button type="button" onClick={removeVoucher} className="text-[11px] font-bold text-red-500">Lepas</button>
                    </div>
                    <div className="mt-2 flex justify-between text-xs">
                      <span className="text-muted-foreground">Potongan</span>
                      <b className="text-emerald-500">-{rupiah(voucherDiscount)}</b>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button data-testid="pay-cash" disabled={!cashEnabled} onClick={() => setMethod("cash")}
                  className={`rounded-2xl border-2 p-3 text-left transition-colors disabled:opacity-40 ${method === "cash" ? "border-primary bg-primary/5" : "border-border bg-muted"}`}>
                  <div className="text-2xl leading-none">💵</div>
                  <div className="mt-1.5 text-sm font-bold text-card-foreground">Cash</div>
                  <div className="text-xs text-muted-foreground">Bayar di kasir</div>
                </button>
                <button data-testid="pay-qris" onClick={() => setMethod("qris")}
                  className={`rounded-2xl border-2 p-3 text-left transition-colors ${method === "qris" ? "border-primary bg-primary/5" : "border-border bg-muted"}`}>
                  <div className="text-2xl leading-none">📱</div>
                  <div className="mt-1.5 text-sm font-bold text-card-foreground">QRIS</div>
                  <div className="text-xs text-muted-foreground">Scan & bayar</div>
                </button>
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-border bg-muted p-4 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>Subtotal</span>
                <span>{rupiah(total)}</span>
              </div>
              {voucherDiscount > 0 && (
                <div className="mt-2 flex justify-between text-emerald-500">
                  <span>Voucher {appliedVoucher?.code}</span>
                  <b>-{rupiah(voucherDiscount)}</b>
                </div>
              )}
              <div className="mt-3 flex justify-between border-t border-border pt-3">
                <b className="text-card-foreground">Total Bayar</b>
                <b className="font-heading text-lg text-primary">{rupiah(finalTotal)}</b>
              </div>
            </div>

            <div className="sticky bottom-0 mt-5 bg-card pt-3">
              <button data-testid="submit-order-btn" disabled={submitting} onClick={submitOrder}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-4 font-bold text-primary-foreground transition-transform active:scale-95 disabled:opacity-50">
                {submitting ? <Loader2 className="wm-spin" size={18} /> : <>Buat Pesanan · {rupiah(finalTotal)}</>}
              </button>
            </div>
          </Sheet>
        )}
        {showHistory && (
          <Sheet onClose={() => setShowHistory(false)}>
            <h2 className="font-heading text-2xl font-extrabold tracking-tight text-card-foreground">Riwayat Pesanan</h2>
            <p className="mt-1 text-sm text-muted-foreground">3 pesanan terbaru</p>
            {historyItems === null && <div className="flex justify-center py-8"><Loader2 className="wm-spin text-muted-foreground" /></div>}
            {historyItems?.length === 0 && <div className="py-10 text-center text-sm text-muted-foreground">Belum ada pesanan.</div>}
            <div className="mt-4 space-y-3">
              {historyItems?.slice(0, 3).map((o) => (
                <div key={o.id} data-testid={`hist-${o.id}`} className="rounded-2xl border border-border bg-muted p-4">
                  <div className="flex items-center justify-between">
                    <b className="font-heading text-sm text-card-foreground">{o.id}</b>
                    <b className="font-heading text-base text-primary">{rupiah(o.total)}</b>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{o.table_name} · {new Date(o.created_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <span className="rounded-full bg-background px-2 py-0.5 text-[10px] font-bold uppercase text-muted-foreground">{HIST_STATUS[o.status] || o.status}</span>
                    <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold uppercase text-primary">{o.payment_method}</span>
                    <span className="rounded-full bg-background px-2 py-0.5 text-[10px] font-bold uppercase text-muted-foreground">{HIST_PAY[o.payment_status] || o.payment_status}</span>
                  </div>
                  {o.payment_method === "qris" && o.payment_status === "pending" && (
                    <button data-testid={`resume-${o.id}`} onClick={() => resumePay(o)} className="mt-3 w-full rounded-xl bg-primary py-2.5 text-sm font-bold text-primary-foreground transition-transform active:scale-95">Lanjutkan Pembayaran</button>
                  )}
                  <div className="mt-3 space-y-2 border-t border-border pt-3">
                    {o.items.map((it, x) => (
                      <div key={x} className="flex gap-2">
                        <div className="h-11 w-11 flex-none overflow-hidden rounded-lg bg-background">{it.image_url ? <img src={it.image_url} alt={it.name} className="h-full w-full object-cover" style={productImageStyle(it)} /> : <div className="grid h-full place-items-center text-muted-foreground"><Store size={14} /></div>}</div>
                        <div className="min-w-0 flex-1">
                          <div className="flex justify-between gap-2"><span className="text-xs font-semibold text-card-foreground">{it.qty}× {it.name}</span><span className="text-xs font-bold text-card-foreground">{rupiah(it.line_total || it.unit_price * it.qty)}</span></div>
                          <div className="text-[10px] text-muted-foreground">Dasar {rupiah(baseOf(it) * it.qty)}</div>
                          {it.modifiers?.map((m, mi) => <div key={mi} className="text-[10px] text-muted-foreground">+ {m.name}×{m.qty}{m.price_delta ? ` · ${rupiah(m.price_delta * m.qty)}` : ""}</div>)}
                          {it.note && <div className="text-[10px] italic text-muted-foreground">"{it.note}"</div>}
                        </div>
                      </div>
                    ))}
                    {Number(o.discount_amount || 0) > 0 && (
                      <div className="flex justify-between border-t border-border pt-2 text-xs text-emerald-500">
                        <span>Voucher {o.voucher_code}</span>
                        <b>-{rupiah(o.discount_amount)}</b>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-border pt-2 text-sm"><b className="text-card-foreground">Total</b><b className="text-primary">{rupiah(o.total)}</b></div>
                  </div>
                  {o.status === "completed" && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <button data-testid={`invoice-dl-${o.id}`} onClick={() => setInvoiceOrder(o)} className="rounded-xl bg-muted py-2.5 text-xs font-bold text-card-foreground">📄 Unduh Invoice</button>
                      <button data-testid={`invoice-wa-${o.id}`} disabled={o.invoice_sent} onClick={() => sendInvoiceWa(o)} className="rounded-xl bg-primary py-2.5 text-xs font-bold text-primary-foreground disabled:opacity-50">{o.invoice_sent ? "✓ Terkirim WA" : "Kirim ke WA"}</button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {historyItems?.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setShowHistory(false);
                  navigate("/history");
                }}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 text-sm font-bold text-primary-foreground transition-transform active:scale-95"
              >
                Lihat Semua Riwayat
                <ChevronRight size={17} />
              </button>
            )}
          </Sheet>
        )}
        <ConfirmEditOrder
          open={showEditOrderConfirm}
          onCancel={() => setShowEditOrderConfirm(false)}
          onConfirm={() => {
            setShowEditOrderConfirm(false);
            setCheckout(false);
          }}
        />

        {invoiceOrder && <InvoiceView o={invoiceOrder} store={data.store} onClose={() => setInvoiceOrder(null)} />}
        {waModal && (
          <div className="fixed inset-0 z-[70] mx-auto flex max-w-[480px] items-center justify-center p-6">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setWaModal(null)} />
            <div className="relative w-full rounded-3xl border border-border bg-card p-6">
              <h3 className="font-heading text-xl font-extrabold text-card-foreground">Kirim Invoice ke WhatsApp</h3>
              <p className="mt-1 text-xs text-muted-foreground">Hanya bisa dikirim <b>1x</b>. Format bebas: 08…, 628…, atau +628…</p>
              <input data-testid="wa-invoice-input" value={waModal.phone} onChange={(e) => setWaModal({ ...waModal, phone: e.target.value })} inputMode="tel" placeholder="Contoh: 081373739090"
                className="mt-4 w-full rounded-2xl border border-border bg-muted p-3.5 text-sm outline-none" />
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button onClick={() => setWaModal(null)} className="rounded-xl bg-muted py-3 text-sm font-bold text-card-foreground">Batal</button>
                <button data-testid="wa-invoice-send" onClick={submitWa} disabled={sendingWa} className="flex items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-bold text-primary-foreground disabled:opacity-50">{sendingWa ? <Loader2 className="wm-spin" size={16} /> : "Kirim"}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ThemeScope>
  );
}

function ConfirmEditOrder({ open, onCancel, onConfirm }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] mx-auto flex max-w-[480px] items-center justify-center p-5">
      <button
        type="button"
        aria-label="Tutup konfirmasi"
        onClick={onCancel}
        className="absolute inset-0 bg-black/65 backdrop-blur-sm"
      />

      <div className="relative w-full rounded-3xl border border-border bg-card p-5 shadow-2xl">
        <button
          type="button"
          onClick={onCancel}
          className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full bg-muted text-card-foreground"
          aria-label="Tutup"
        >
          <X size={18} />
        </button>

        <div className="pr-10">
          <h3 className="font-heading text-xl font-extrabold text-card-foreground">
            Ubah Pesanan?
          </h3>

          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Apakah Anda yakin akan mengubah pesanan?
          </p>

          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            Isi keranjang tidak akan hilang. Anda akan kembali ke menu untuk
            menambah, mengurangi, atau mengganti pesanan.
          </p>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl bg-muted py-3 text-sm font-bold text-card-foreground"
          >
            Batal
          </button>

          <button
            type="button"
            data-testid="confirm-edit-order-btn"
            onClick={onConfirm}
            className="rounded-xl bg-primary py-3 text-sm font-bold text-primary-foreground"
          >
            Ya, Ubah Pesanan
          </button>
        </div>
      </div>
    </div>
  );
}

function baseOf(it) { return it.unit_price - (it.modifiers || []).reduce((s, m) => s + (m.price_delta || 0) * m.qty, 0); }

function InvoiceView({ o, store, onClose }) {
  const paid = o.payment_status === "paid";
  return (
    <div className="fixed inset-0 z-[60] mx-auto flex max-w-[480px] items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/60 no-print" onClick={onClose} />
      <div className="inv-print relative max-h-[90dvh] w-full overflow-y-auto rounded-t-3xl bg-white text-neutral-900 sm:rounded-3xl">
        <div className="flex items-center gap-3 bg-neutral-900 px-6 py-5 text-white">
          {store.logo_url && (
            <div className={`h-14 w-14 shrink-0 overflow-hidden bg-white/10 ${store.logo_shape === "circle" ? "rounded-full" : "rounded-xl"}`}>
              <img src={store.logo_url} alt="" className="h-full w-full object-cover" />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-heading text-xl font-extrabold leading-tight">{store.name}</div>
            {store.address && <div className="text-[11px] leading-snug text-white/60">{store.address}</div>}
          </div>
        </div>

        <div className="px-6 pb-6 pt-5">
          <div className="mb-2 font-heading text-lg font-extrabold tracking-wide">INVOICE</div>
          <div className="space-y-1 text-xs text-neutral-500">
            <div className="flex justify-between"><span>No. Invoice</span><b className="text-neutral-800">{o.id}</b></div>
            <div className="flex justify-between"><span>Tanggal</span><span className="text-neutral-800">{new Date(o.created_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}</span></div>
            <div className="flex justify-between"><span>Meja</span><span className="text-neutral-800">{o.table_name}</span></div>
            <div className="flex justify-between"><span>Pelanggan</span><span className="text-neutral-800">{o.customer_name}</span></div>
          </div>

          <div className="mt-4 border-t border-dashed border-neutral-300 pt-3 text-sm">
            {o.items.map((it, x) => (
              <div key={x} className="mb-2.5">
                <div className="flex justify-between font-semibold"><span>{it.qty}× {it.name}</span><span>{rupiah(it.line_total || it.unit_price * it.qty)}</span></div>
                {it.modifiers?.map((m, mi) => <div key={mi} className="flex justify-between pl-3 text-[11px] text-neutral-500"><span>+ {m.name}×{m.qty}</span><span>{m.price_delta ? rupiah(m.price_delta * m.qty) : "gratis"}</span></div>)}
                {it.note && <div className="pl-3 text-[10px] italic text-neutral-400">"{it.note}"</div>}
              </div>
            ))}
          </div>

          {Number(o.discount_amount || 0) > 0 && (
            <div className="mt-2 rounded-xl bg-emerald-50 px-4 py-3 text-xs">
              <div className="flex justify-between text-neutral-600"><span>Subtotal</span><span>{rupiah(o.subtotal || o.total)}</span></div>
              <div className="mt-1 flex justify-between font-bold text-emerald-700"><span>Voucher {o.voucher_code}</span><span>-{rupiah(o.discount_amount)}</span></div>
            </div>
          )}

          <div className="mt-2 flex items-center justify-between rounded-xl bg-neutral-900 px-4 py-3 text-white">
            <span className="font-heading text-base font-extrabold">TOTAL</span>
            <span className="font-heading text-base font-extrabold">{rupiah(o.total)}</span>
          </div>

          <div className="mt-3 flex items-center gap-2 text-xs">
            <span className="text-neutral-500">Pembayaran</span>
            <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${paid ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}>{paid ? "LUNAS" : "BELUM LUNAS"}</span>
            <span className="text-neutral-500">· {o.payment_method.toUpperCase()}</span>
          </div>

          <div className="mt-4 text-center text-[11px] text-neutral-400">Terima kasih atas pesanan Anda 🙏</div>

          <div className="mt-4 grid grid-cols-2 gap-2 no-print">
            <button onClick={onClose} className="rounded-xl bg-neutral-100 py-2.5 text-sm font-bold text-neutral-700">Tutup</button>
            <button data-testid="invoice-print-btn" onClick={() => window.print()} className="rounded-xl bg-neutral-900 py-2.5 text-sm font-bold text-white">Cetak / Simpan PDF</button>
          </div>
        </div>
      </div>
      <style>{`@media print{@page{margin:0}html,body{background:#fff!important}body *{visibility:hidden!important}.inv-print,.inv-print *{visibility:visible!important}.inv-print{position:absolute;inset:0;max-height:none;border-radius:0}.no-print{display:none!important}}`}</style>
    </div>
  );
}

const HIST_STATUS = { new: "Baru", preparing: "Diproses", ready: "Siap", completed: "Selesai", cancelled: "Batal" };
const HIST_PAY = { unpaid: "Belum Bayar", pending: "Menunggu", paid: "Lunas", failed: "Gagal" };

function Sheet({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-40 mx-auto flex max-w-[480px] flex-col justify-end">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="sheet-backdrop" />
      <div className="relative max-h-[88dvh] overflow-y-auto rounded-t-[2rem] border-t border-border bg-card p-6" style={{ animation: "wm-sheet .28s cubic-bezier(.16,1,.3,1)" }}>
        <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-muted-foreground/30" />
        <button onClick={onClose} className="absolute right-5 top-5 grid h-9 w-9 place-items-center rounded-full bg-muted text-card-foreground"><X size={18} /></button>
        {children}
      </div>
    </div>
  );
}
